import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check, TrendingUp, Package, MessageSquare, FileJson } from 'lucide-react';
import { toast } from 'sonner';

export default function DocsExamples() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Platform Integration Examples
  const shopifyLoader = `{% comment %} Add this to theme.liquid before </body> {% endcomment %}
<script>
  (function(w, d, s, src, name) {
    if (w[name]) { return; }
    var ci = w[name] = function() { ci.q.push(arguments); };
    ci.q = [];
    var el = d.createElement(s);
    el.async = true;
    el.src = src;
    var firstScript = d.getElementsByTagName(s)[0];
    firstScript.parentNode.insertBefore(el, firstScript);
  })(window, document, 'script', 'https://cdn.commerceindex.com/ci.js', 'ci');

  ci('init', { brandId: '{{ shop.permanent_domain | md5 }}' });
</script>`;

  const shopifyPDP = `{% comment %} Add this to product.liquid or main-product.liquid {% endcomment %}
<script>
  document.addEventListener('DOMContentLoaded', function() {
    ci('trackView', {
      productId: '{{ product.id }}',
      productSku: '{{ product.selected_or_first_available_variant.sku | escape }}',
      productTitle: '{{ product.title | escape }}',
      productUrl: '{{ shop.url }}{{ product.url }}'
    });
  });
</script>`;

  const shopifyPurchase = `{% comment %} Add this to checkout.liquid or order confirmation page {% endcomment %}
{% if first_time_accessed %}
<script>
  ci('trackPurchase', {
    orderId: '{{ order.id }}',
    currency: '{{ order.currency }}',
    items: [
      {% for line_item in order.line_items %}
      {
        productId: '{{ line_item.product.id }}',
        productSku: '{{ line_item.sku | escape }}',
        productTitle: '{{ line_item.title | escape }}',
        quantity: {{ line_item.quantity }},
        value: {{ line_item.final_price | divided_by: 100.0 }}
      }{% unless forloop.last %},{% endunless %}
      {% endfor %}
    ]
  });
</script>
{% endif %}`;

  const nextjsHook = `// hooks/useCommerceIndex.js
import { useEffect } from 'react';

export function useTrackView(product) {
  useEffect(() => {
    if (typeof window !== 'undefined' && window.ci && product?.id) {
      window.ci('trackView', {
        productId: product.id,
        productSku: product.sku,
        productTitle: product.title,
        productUrl: window.location.href
      });
    }
  }, [product?.id]);
}

export function trackPurchase(order) {
  if (typeof window !== 'undefined' && window.ci) {
    window.ci('trackPurchase', {
      orderId: order.id,
      currency: order.currency,
      items: order.lineItems.map(item => ({
        productId: item.productId,
        productSku: item.sku,
        productTitle: item.title,
        quantity: item.quantity,
        value: item.price
      }))
    });
  }
}`;

  const nextjsComponent = `// components/ProductPage.jsx
import { useTrackView } from '../hooks/useCommerceIndex';

export default function ProductPage({ product }) {
  // Track view when product loads
  useTrackView(product);

  return (
    <div>
      <h1>{product.title}</h1>
      <p>{product.description}</p>
      <span>\${product.price}</span>
      <button onClick={() => addToCart(product)}>
        Add to Cart
      </button>
    </div>
  );
}`;

  const serverSideExample = `// Server-side tracking example (Node.js)
const axios = require('axios');

const CI_API_URL = 'https://commerceindex.com/api';
const CI_API_KEY = process.env.CI_API_KEY;
const CI_BRAND_ID = process.env.CI_BRAND_ID;

// Track purchase after order is created
async function trackPurchase(order) {
  try {
    const response = await axios.post(
      CI_API_URL + '/collect/product-purchase',
      {
        brand_id: CI_BRAND_ID,
        order: {
          order_id: order.id,
          currency: order.currency,
          total_value: order.total,
          items: order.items.map(item => ({
            product_id: item.productId,
            sku: item.sku,
            title: item.title,
            quantity: item.quantity,
            unit_price: item.price
          }))
        },
        context: {
          timestamp: new Date().toISOString()
        }
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CI-Api-Key': CI_API_KEY
        }
      }
    );
    
  } catch (error) {
    console.error('Failed to track purchase:', error.message);
    // Don't block the order process on CI failures
  }
}`;

  // Signals Recipes
  const brandSignalsNode = `// brand-signals.js
const fetch = require('node-fetch'); // or global fetch in newer Node

const API_BASE = 'https://commerceindex.com/api';
const API_KEY = process.env.CI_API_KEY;      // set in env
const BRAND_ID = 'brand_xyz';

async function fetchBrandSignals() {
  const url = \`\${API_BASE}/v1/signals/brand?brand_id=\${BRAND_ID}&window=30d\`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'X-CI-Api-Key': API_KEY,
      'Content-Type': 'application/json'
    }
  });

  if (!res.ok) {
    console.error(\`Error: \${res.status} \${res.statusText}\`);
    const errBody = await res.text();
    console.error(errBody);
    process.exit(1);
  }

  const data = await res.json();
  const agg = data.aggregate;
  const trend = data.trend;

  (data.top_products || []).forEach((p, idx) => {
      \`\${idx + 1}. \${p.title} – views: \${p.views}, purchases: \${p.purchases}, conv: \${(p.conversion_rate * 100).toFixed(2)}%, trend: \${p.trend}\`
    );
  });
}

fetchBrandSignals().catch(err => {
  console.error('Unexpected error:', err);
});`;

  const productSignalsPython = `# product_signals.py
import os
import requests

API_BASE = "https://commerceindex.com/api"
API_KEY = os.environ.get("CI_API_KEY")
BRAND_ID = "brand_xyz"
PRODUCT_ID = "prod_123"

def fetch_product_signals():
    url = f"{API_BASE}/v1/signals/product"
    params = {
        "brand_id": BRAND_ID,
        "product_id": PRODUCT_ID,
        "window": "7d"
    }
    headers = {
        "X-CI-Api-Key": API_KEY,
        "Content-Type": "application/json"
    }

    resp = requests.get(url, params=params, headers=headers)
    if resp.status_code != 200:
        print("Error:", resp.status_code, resp.text)
        return

    data = resp.json()
    activity = data.get("activity", {})
    trend = data.get("trend", {})
    signal = data.get("signal", {})

    print(f"Brand:   {data.get('brand_id')}")
    print(f"Product: {data.get('product', {}).get('title')} ({data.get('product', {}).get('product_id')})")
    print(f"Window:  {data.get('time_window')}")
    print("")
    print(f"Views:      {activity.get('views')}")
    print(f"Purchases:  {activity.get('purchases')}")
    print(f"Conversion: {round(activity.get('conversion_rate', 0) * 100, 2)}%")
    print("")
    print(f"Views vs prev:      {trend.get('views_vs_prev_period')} ({round(trend.get('views_change_pct', 0), 1)}%)")
    print(f"Purchases vs prev:  {trend.get('purchases_vs_prev_period')} ({round(trend.get('purchases_change_pct', 0), 1)}%)")
    print(f"Conversion vs prev: {trend.get('conversion_vs_prev_period')} ({round(trend.get('conversion_change_pct', 0), 1)}%)")
    print("")
    print(f"Activity tier: {signal.get('activity_tier')}")
    print(f"Comment:       {signal.get('comment')}")

if __name__ == "__main__":
    fetch_product_signals()`;

  const contextSignalsNode = `// context-signals.js
const fetch = require('node-fetch');

const API_BASE = 'https://commerceindex.com/api';
const API_KEY = process.env.CI_API_KEY;
const BRAND_ID = 'brand_xyz';
const PRODUCT_ID = 'prod_123';

async function fetchContext() {
  const url = \`\${API_BASE}/v1/signals/context\`;

  const body = {
    brand_id: BRAND_ID,
    product_id: PRODUCT_ID,
    country: "US",
    window: "7d"
  };

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'X-CI-Api-Key': API_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    console.error(\`Error: \${res.status} \${res.statusText}\`);
    const errBody = await res.text();
    console.error(errBody);
    process.exit(1);
  }

  const data = await res.json();


  (data.key_signals || []).forEach(sig => {
    if (sig.details) {
    }
  });

  if (data.recommendations && data.recommendations.length > 0) {
    data.recommendations.forEach((rec, idx) => {
    });
  }

}

fetchContext().catch(err => {
  console.error('Unexpected error:', err);
});`;

  const brandSignalsCSV = `# brand_signals_to_csv.py
import os
import csv
import requests

API_BASE = "https://commerceindex.com/api"
API_KEY = os.environ.get("CI_API_KEY")
BRAND_ID = "brand_xyz"

def main():
    url = f"{API_BASE}/v1/signals/brand"
    params = {
        "brand_id": BRAND_ID,
        "window": "30d"
    }
    headers = {
        "X-CI-Api-Key": API_KEY,
        "Content-Type": "application/json"
    }

    resp = requests.get(url, params=params, headers=headers)
    resp.raise_for_status()
    data = resp.json()

    top_products = data.get("top_products", [])

    with open("top_products_signals.csv", "w", newline="") as f:
        writer = csv.writer(f)
        writer.writerow(["product_id", "title", "views", "purchases", "conversion_rate", "trend"])
        for p in top_products:
            writer.writerow([
                p.get("product_id"),
                p.get("title"),
                p.get("views"),
                p.get("purchases"),
                round(p.get("conversion_rate", 0) * 100, 2),
                p.get("trend")
            ])

    print("Wrote top_products_signals.csv")

if __name__ == "__main__":
    main()`;

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 mb-4">
          Resources
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Examples & Recipes
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Platform-specific integration guides, code examples, and Signals API recipes.
        </p>
      </div>

      {/* Signals Recipes Section */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-6">
          <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30">
            Signals API
          </Badge>
          <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Signals Recipes
          </h2>
        </div>
        
        <div className="p-4 bg-[#0D1628] rounded-lg border border-[#22304A] mb-6">
          <p className="text-[#94A3B8] text-sm">
            These recipes show how to use Commerce Index Signals in simple scripts and tools. All examples assume 
            you have already integrated CI Proof (view and purchase events) and have an API key.
          </p>
          <div className="mt-3 pt-3 border-t border-[#22304A]">
            <p className="text-[#64748B] text-xs">
              <strong>Prerequisites:</strong> A <code className="text-[#3EF1C7]">brand_id</code> assigned by Commerce Index, 
              an API key (<code className="text-[#3EF1C7]">X-CI-Api-Key</code>), and active Proof integration.
            </p>
          </div>
        </div>

        {/* Recipe 1: Brand Signals */}
        <Card className="bg-[#0D1628] border-[#22304A] mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-[#3EF1C7]" />
              <CardTitle className="text-white">1. Brand Signals – CLI Summary (Node.js)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[#94A3B8] mb-2 text-sm">
              <strong className="text-white">File:</strong> brand-signals.js
            </p>
            <p className="text-[#94A3B8] mb-4 text-sm">
              Fetches brand-level activity for the last 30 days and prints a simple summary: total views, 
              total purchases, conversion rate, trends vs the previous 30-day window, and top products by views/purchases.
            </p>
            <p className="text-[#64748B] text-xs mb-4">
              Use this to quickly get a high-level pulse of how your brand is doing without opening any dashboards.
            </p>
            <div className="relative">
              <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0] max-h-96">
                <code>{brandSignalsNode}</code>
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                onClick={() => copyToClipboard(brandSignalsNode, 'brand-signals-node')}
              >
                {copiedCode === 'brand-signals-node' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[#64748B] text-xs mt-3">
              Run: <code className="text-[#3EF1C7]">CI_API_KEY=your_api_key_here node brand-signals.js</code>
            </p>
          </CardContent>
        </Card>

        {/* Recipe 2: Product Signals */}
        <Card className="bg-[#0D1628] border-[#22304A] mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-[#7A5CFF]" />
              <CardTitle className="text-white">2. Product Signals – Is This Product Hot or Cold? (Python)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[#94A3B8] mb-2 text-sm">
              <strong className="text-white">File:</strong> product_signals.py
            </p>
            <p className="text-[#94A3B8] mb-4 text-sm">
              Calls the Product Signals endpoint for a single product and prints: views, purchases, conversion rate, 
              trend vs the previous period, an activity tier (high/medium/low) and a short comment.
            </p>
            <p className="text-[#64748B] text-xs mb-4">
              Use this when you want to evaluate individual SKUs and spot which products are outperforming or underperforming.
            </p>
            <div className="relative">
              <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0] max-h-96">
                <code>{productSignalsPython}</code>
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                onClick={() => copyToClipboard(productSignalsPython, 'product-signals-python')}
              >
                {copiedCode === 'product-signals-python' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[#64748B] text-xs mt-3">
              Run: <code className="text-[#3EF1C7]">CI_API_KEY=your_api_key_here python product_signals.py</code>
            </p>
          </CardContent>
        </Card>

        {/* Recipe 3: Context Signals */}
        <Card className="bg-[#0D1628] border-[#22304A] mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-[#FFD166]" />
              <CardTitle className="text-white">3. Context Signals – One-Line Summary for Agents/Tools (Node.js)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[#94A3B8] mb-2 text-sm">
              <strong className="text-white">File:</strong> context-signals.js
            </p>
            <p className="text-[#94A3B8] mb-4 text-sm">
              Posts a brand + product to the Context API and prints: a one-line headline, key signals 
              (activity, trend, brand background), and optional recommendations.
            </p>
            <p className="text-[#64748B] text-xs mb-4">
              This is ideal for agents, assistants, or internal tools that need a compact, opinionated summary instead of raw statistics.
            </p>
            <div className="relative">
              <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0] max-h-96">
                <code>{contextSignalsNode}</code>
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                onClick={() => copyToClipboard(contextSignalsNode, 'context-signals-node')}
              >
                {copiedCode === 'context-signals-node' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[#64748B] text-xs mt-3">
              Run: <code className="text-[#3EF1C7]">CI_API_KEY=your_api_key_here node context-signals.js</code>
            </p>
          </CardContent>
        </Card>

        {/* Recipe 4: Brand Signals to CSV */}
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileJson className="w-5 h-5 text-[#3EF1C7]" />
              <CardTitle className="text-white">4. Brand Signals → CSV for BI (Python)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[#94A3B8] mb-2 text-sm">
              <strong className="text-white">File:</strong> brand_signals_to_csv.py
            </p>
            <p className="text-[#94A3B8] mb-4 text-sm">
              Fetches Brand Signals and writes the <code className="text-[#3EF1C7]">top_products</code> block to 
              <code className="text-[#3EF1C7]"> top_products_signals.csv</code>, with views, purchases, conversion rate, and trend per product.
            </p>
            <p className="text-[#64748B] text-xs mb-4">
              Use this to pull CI Signals into your BI environment or spreadsheets for further analysis.
            </p>
            <div className="relative">
              <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0] max-h-80">
                <code>{brandSignalsCSV}</code>
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                onClick={() => copyToClipboard(brandSignalsCSV, 'brand-signals-csv')}
              >
                {copiedCode === 'brand-signals-csv' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Signals Summary */}
        <div className="mt-6 p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
          <p className="text-[#94A3B8] text-sm">
            Each example uses <code className="text-[#3EF1C7]">CI_API_KEY</code> read from an environment variable and the 
            production Signals endpoints (<code className="text-[#3EF1C7]">/v1/signals/brand</code>, <code className="text-[#3EF1C7]">/v1/signals/product</code>, <code className="text-[#3EF1C7]">/v1/signals/context</code>).
            You can adapt these scripts to your own stack (Node, Python, serverless functions, or backend jobs) 
            to embed Commerce Index Signals into your workflows.
          </p>
        </div>
      </section>

      {/* Platform Integration Section */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Platform Integration
        </h2>

        {/* Shopify */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">Shopify</h3>
          
          <div className="space-y-6">
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <CardTitle className="text-white">Step 1: Add Loader to Theme</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] mb-4">
                  Add this to <code className="text-[#3EF1C7] bg-[#3EF1C7]/10 px-1 rounded">theme.liquid</code> before 
                  the closing <code className="text-[#3EF1C7] bg-[#3EF1C7]/10 px-1 rounded">&lt;/body&gt;</code> tag.
                </p>
                <div className="relative">
                  <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                    <code>{shopifyLoader}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                    onClick={() => copyToClipboard(shopifyLoader, 'shopify-loader')}
                  >
                    {copiedCode === 'shopify-loader' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <CardTitle className="text-white">Step 2: Track Product Views</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] mb-4">
                  Add this to your product template.
                </p>
                <div className="relative">
                  <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                    <code>{shopifyPDP}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                    onClick={() => copyToClipboard(shopifyPDP, 'shopify-pdp')}
                  >
                    {copiedCode === 'shopify-pdp' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <CardTitle className="text-white">Step 3: Track Purchases</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] mb-4">
                  Add this to the order confirmation page (requires Shopify Plus for checkout.liquid access).
                </p>
                <div className="relative">
                  <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                    <code>{shopifyPurchase}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                    onClick={() => copyToClipboard(shopifyPurchase, 'shopify-purchase')}
                  >
                    {copiedCode === 'shopify-purchase' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Next.js / React */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">Next.js / React</h3>
          
          <Tabs defaultValue="hook" className="w-full">
            <TabsList className="bg-[#1E293B] border border-[#334155] mb-4">
              <TabsTrigger value="hook" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                Custom Hook
              </TabsTrigger>
              <TabsTrigger value="component" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                Component Usage
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="hook">
              <Card className="bg-[#0D1628] border-[#22304A]">
                <CardHeader>
                  <CardTitle className="text-white">Commerce Index Hook</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                      <code>{nextjsHook}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                      onClick={() => copyToClipboard(nextjsHook, 'nextjs-hook')}
                    >
                      {copiedCode === 'nextjs-hook' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="component">
              <Card className="bg-[#0D1628] border-[#22304A]">
                <CardHeader>
                  <CardTitle className="text-white">Product Page Component</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                      <code>{nextjsComponent}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                      onClick={() => copyToClipboard(nextjsComponent, 'nextjs-component')}
                    >
                      {copiedCode === 'nextjs-component' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Server-Side */}
        <div>
          <h3 className="text-xl font-semibold text-white mb-4">Server-Side (Headless)</h3>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-white">Node.js Example</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] mb-4">
                For headless setups, you can track purchases server-side after order creation.
              </p>
              <div className="relative">
                <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                  <code>{serverSideExample}</code>
                </pre>
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                  onClick={() => copyToClipboard(serverSideExample, 'server-side')}
                >
                  {copiedCode === 'server-side' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
